import { z } from 'zod'
import { isAgentAuthoredComment } from '../../core/entities/comment.js'
import { baseEntitySchema } from '../../core/entity.js'

/**
 * Wall-clock ceiling for a single agent turn, in seconds. The single source
 * of truth consumed by BOTH the agent-stream Lambda's CDK timeout and the
 * client's runtime-status staleness cutoff — so the client can never assume
 * a turn is dead while the server is still allowed to run it.
 */
export const AGENT_TURN_MAX_SECONDS = 180

/**
 * Voices an agent can take (Personalize agents, spec #2365 §1). Mirrors ai-lib's `personalities` so
 * space settings and agents share one vocabulary; a drift test in ai-lib pins the two lists equal.
 * Duplicated rather than imported because `schemas/` must not depend on ai-lib.
 */
export const agentVoices = ['neutral', 'friendly', 'professional', 'witty', 'creative'] as const
export type AgentVoice = (typeof agentVoices)[number]
export const agentVoiceSchema = z.enum(agentVoices)

/**
 * What each voice tells the model (Bowie; matched to Joni's UI descriptions in #2373). The one source
 * for the agent preamble and for ai-lib's `personality` setting, so a word means the same thing in both.
 *
 * `creative` is bounded on purpose: it adds one labelled less-obvious idea inside the song's key and
 * style, and never changes what the agent suggests by default. A band that wants out-there harmony
 * says so in `instructions`.
 */
export const agentVoiceLines: Record<AgentVoice, string> = {
  neutral: 'Be plain and to the point.',
  friendly: 'Speak warmly and informally, like a bandmate.',
  professional: 'Be concise and matter-of-fact, like a session player.',
  witty: 'A little dry humour is welcome; never at the expense of the answer.',
  creative:
    "Alongside the expected suggestion, offer one less obvious idea that still fits the song's key and style, and say which is which.",
}

/** The cap on an agent's `instructions` (spec #2365, decided by the user): a cost bound and a quality bound. */
export const agentInstructionsMaxLength = 600

/**
 * The write-time check on an agent's name: trimmed, 1–40 characters, one line, no control characters.
 * The name reaches the model prompt, so the server enforces it on every agent write (`parseAgentPersonaWrite`),
 * not only the form. Not applied by narrowing `name` on the shared base entity schema, which every entity
 * type parses.
 */
export const agentNameSchema = z
  .string()
  .trim()
  .min(1, 'An agent name needs at least one character')
  .max(40, 'An agent name is at most 40 characters')
  .regex(/^[^\u0000-\u001f\u007f]*$/, 'An agent name is one line of plain text')

/**
 * The persona fields an agent write may carry, each validated and normalised (trimmed). The server's
 * entity write paths run every agent create, update and upsert through this. A persona field that is
 * present must be valid; absent fields are untouched. `voice: null` clears the voice ("Usual").
 */
export const agentPersonaWriteSchema = z.object({
  name: agentNameSchema.optional(),
  voice: agentVoiceSchema.nullable().optional(),
  instructions: z
    .string()
    .trim()
    .max(agentInstructionsMaxLength, `Instructions are at most ${agentInstructionsMaxLength} characters`)
    .nullable()
    .optional(),
})
export type AgentPersonaWrite = z.infer<typeof agentPersonaWriteSchema>

const personaKeys = ['name', 'voice', 'instructions'] as const

/**
 * Validate the persona fields of an agent write. Returns the normalised fields that were present, or the
 * first problem in words. Fields outside the persona pass through untouched by this check.
 */
export const parseAgentPersonaWrite = (
  write: Record<string, unknown>
): { ok: true; persona: AgentPersonaWrite } | { ok: false; message: string } => {
  const present = Object.fromEntries(personaKeys.filter(key => key in write).map(key => [key, write[key]]))
  const parsed = agentPersonaWriteSchema.safeParse(present)
  if (!parsed.success) {
    return { ok: false, message: parsed.error.issues[0]?.message ?? 'Invalid agent persona' }
  }
  return { ok: true, persona: parsed.data }
}

/**
 * The name an agent is created with, by definition. One source for the app-side attach path
 * (`resolve-or-attach-agent.ts`) and the rule-driven link (`link-agent.ts`), so a rule-created agent is
 * indistinguishable from a user-created one.
 */
export const defaultAgentName = (definitionId: string): string => {
  switch (definitionId) {
    case 'song-collaborator':
      return 'Songwriting Collaborator'
    case 'chord-advisor':
      return 'Chord Advisor'
    case 'lyric-writer':
      return 'Lyric Writer'
    case 'songwriting-advisor':
      return 'Songwriting Advisor'
    case 'cover-song-importer':
      return 'Cover Song Importer'
    default:
      return 'Agent'
  }
}

const normaliseName = (name: string) => name.trim().toLowerCase()

/**
 * Whether an agent still has the name it was created with, i.e. the band never named it. Every agent is
 * created with a name (`defaultAgentName`, or the definition's own name from the palette path), so the
 * prompt may carry the name only when this is false — otherwise every existing agent's preamble would
 * change (Personalize agents, spec #2365 §2: absent persona, byte-identical prompt).
 */
export const isDefaultAgentName = (
  agent: { name?: string; definitionId?: string },
  definitionName?: string
): boolean => {
  if (!agent.name?.trim()) {
    return true
  }
  const name = normaliseName(agent.name)
  const defaults = [defaultAgentName(agent.definitionId ?? ''), definitionName].filter(
    (value): value is string => typeof value === 'string'
  )
  return defaults.some(value => normaliseName(value) === name)
}

export const agentRoles = ['song-agent', 'project-agent', 'folder-agent', 'generic'] as const
export type AgentRole = (typeof agentRoles)[number]
export const agentRoleSchema = z.enum(agentRoles)

/**
 * The agent role that belongs on a given host entity type.
 *
 * Single source of truth for the two create paths that attach an agent to an
 * entity — `resolveOrAttachAgent` (plugin-agent) and `agentDefinitionToCommand`
 * (plugin-lib) — which previously each carried their own copy. Both must produce
 * the same role for the same host, or the same song ends up with agents of
 * differing roles depending on which path created them. Lives here, next to
 * `agentRoles`, so a server-side create can reach it without a plugin dependency.
 */
export const roleForParent = (parentType: string | undefined): AgentRole => {
  switch (parentType) {
    case 'song':
      return 'song-agent'
    case 'folder':
      return 'folder-agent'
    default:
      return 'generic'
  }
}

export const agentStatuses = ['active', 'paused', 'archived'] as const
export type AgentStatus = (typeof agentStatuses)[number]
export const agentStatusSchema = z.enum(agentStatuses)

/**
 * Live runtime state. Orthogonal to the persistent `status` field
 * (which is the user's intent — `active` / `paused` / `archived`).
 * `runtimeStatus` is "is the agent currently executing a turn?" and
 * is broadcast via WebSocket partial-entity updates without touching
 * DynamoDB. Default-undefined means idle.
 */
export const agentRuntimeStatuses = ['idle', 'thinking', 'error'] as const
export type AgentRuntimeStatus = (typeof agentRuntimeStatuses)[number]
export const agentRuntimeStatusSchema = z.enum(agentRuntimeStatuses)

export const agentProactivityLevels = ['off', 'low', 'normal', 'high'] as const
export type AgentProactivity = (typeof agentProactivityLevels)[number]
export const agentProactivitySchema = z.enum(agentProactivityLevels)

/**
 * Per-user, per-agent "last read" cursors. Keyed by agentId, value is the
 * ISO-8601 timestamp the user last opened that agent's panel. Stored on
 * the user's profile config (cross-device sync free) and consulted by the
 * Agent Rail to decide whether to render an "unread" dot.
 */
export const agentReadCursorsSchema = z.record(
  z.string().describe('agentId'),
  z.string().describe('ISO-8601 timestamp of last read')
)
export type AgentReadCursors = z.infer<typeof agentReadCursorsSchema>

export const agentTriggerConfigSchema = z.object({
  onChange: z.boolean().optional(),
  idleDays: z.number().int().positive().optional(),
  sessionSignals: z.boolean().optional(),
  goals: z.boolean().optional(),
})

export const agentConfigSchema = z.object({
  proactivity: agentProactivitySchema.default('normal'),
  triggers: agentTriggerConfigSchema.default({}),
  allowedTools: z.array(z.string()).optional(),
})

export const agentGoalSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  dueAt: z.string().describe('ISO-8601 deadline').optional(),
  completed: z.boolean().optional(),
  createdAt: z.string().describe('ISO-8601 creation timestamp'),
})
export type AgentGoal = z.infer<typeof agentGoalSchema>

export const agentMemorySchema = z.object({
  summary: z.string().optional(),
  facts: z.array(z.string()).optional(),
  openQuestions: z.array(z.string()).optional(),
})
export type AgentMemory = z.infer<typeof agentMemorySchema>

export const agentSchema = baseEntitySchema.extend({
  type: z.literal('agent'),
  /** Links to the AgentDefinition.id from the plugin that created this agent */
  definitionId: z.string().optional(),
  /** Custom role line for the system prompt preamble. Overrides role-based default. */
  roleLine: z.string().optional(),
  role: agentRoleSchema,
  status: agentStatusSchema.default('active'),
  /** Model id override. Stored as a plain string so schemas/ stays independent of ai-lib. */
  model: z.string().optional(),
  config: agentConfigSchema,
  goals: z.array(agentGoalSchema).optional(),
  memory: agentMemorySchema.optional(),
  /** ISO-8601 timestamp of the most recent proactive run. Used to rate-limit nudges. */
  lastRunAt: z.string().optional(),
  /** ISO-8601 timestamp of the most recent user-visible nudge. */
  lastNudgeAt: z.string().optional(),
  /**
   * Live runtime state — transient, never persisted to DDB.
   * Pushed via WebSocket `receivePartialEntity` envelopes. Default-undefined
   * means idle. See `agentRuntimeStatuses` for axis docs.
   */
  runtimeStatus: agentRuntimeStatusSchema.describe('Transient runtime state').optional(),
  /** ISO-8601 timestamp the runtimeStatus was last asserted. Used for client-side TTL. */
  runtimeStatusAt: z.string().describe('ISO-8601 of last runtime status assert').optional(),
  /** Human-readable error blurb when runtimeStatus === 'error'. */
  runtimeError: z.string().describe('Error blurb for runtimeStatus error').optional(),
  /**
   * Example prompts the user can click as a starter when the agent's chat is empty.
   * Renders as chips in the empty-state header above the chat feed. Plugins authoring
   * an `AgentDefinition` may seed this from their definition; falls back to role-keyed
   * defaults at render time when omitted.
   */
  examplePrompts: z.array(z.string()).describe('Starter prompts for the empty chat state').optional(),
  /**
   * Material color name used by the rail card, sidebar tab marker, palette
   * icon, and chat avatar. Plain `string` rather than an enum because the
   * registry of valid palette names lives in `@tunasong/ui-lib` (importing
   * it here would cross a circular-dep line). Runtime validation against
   * the registry happens at render time via `getColorCSS()`; an unknown
   * value gracefully falls back to `inherit`.
   */
  color: z
    .string()
    .describe('Color token name (one of the material color palette names — see @tunasong/ui-lib getColorCSS).')
    .optional(),
  /**
   * Icon registry key for the agent. Plain `string` rather than an enum
   * for the same reason as `color` — the registry lives in `@tunasong/icons`
   * and lookup is deferred to render time. Unknown values fall back to the
   * role-keyed default.
   */
  iconKey: z.string().describe('Icon registry key for the agent (see @tunasong/icons).').optional(),
  /**
   * How the agent talks (spec #2365 §1). Absent adds nothing to the prompt — every agent that predates
   * this field keeps a byte-identical preamble — so a UI must show it as unset, not as `neutral`.
   */
  voice: agentVoiceSchema
    .nullable()
    .describe('How the agent talks. Absent or null ("Usual") adds no voice line.')
    .optional(),
  /**
   * How this band wants the agent to work, in their words. Composes with the role, never replaces it:
   * the role stays definition-owned (spec #2365, decided by the user). The 600 cap here agrees with the
   * server, which rejects longer writes (`parseAgentPersonaWrite`), so `isAgent` never meets a stored value
   * it would reject; the runtime still truncates defensively. Null means cleared.
   */
  instructions: z
    .string()
    .trim()
    .max(agentInstructionsMaxLength)
    .nullable()
    .describe('How this band wants the agent to work. Composes with, never replaces, the role.')
    .optional(),
  /**
   * Per user: when they last started a new conversation with this agent ("New conversation"). Their panel and
   * the agent's prompt show only what came after it; earlier messages are kept, not deleted. Absent means one
   * continuous conversation, as before the field existed.
   */
  conversationStarts: z
    .record(z.string().describe('userId'), z.string().describe('ISO-8601 time the conversation started'))
    .describe("When each user last started a new conversation with this agent; earlier messages leave their chat")
    .optional(),
})

export type Agent = z.infer<typeof agentSchema>

export function isAgent(node?: unknown): node is Agent {
  return agentSchema.safeParse(node).success
}

/**
 * Whether a chat row belongs to one user's current conversation with an agent: their own messages and the
 * agent's (an advisor chat on a shared song is per-user, GH #2516), after their last "New conversation". The
 * panel and the agent's prompt both use this, so what the user sees is what the agent reads. A row without a
 * timestamp yet (an optimistic send) counts as current.
 */
export const isInUsersAgentChat = (
  row: { userId?: string; kind?: string; metadata?: unknown; createdAt?: string },
  agent: Pick<Agent, 'conversationStarts'>,
  userId: string
): boolean => {
  if (!(row.userId === userId || isAgentAuthoredComment(row))) {
    return false
  }
  const start = agent.conversationStarts?.[userId]
  return !start || row.createdAt === undefined || row.createdAt > start
}
